import { useState } from "react";
import { api, fmtDate, fmtEur, formatErr, CATEGORIE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const lastDay = (mese) => { const [y, m] = mese.split("-").map(Number); return `${mese}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`; };
const DEF_CAT = { spesa: "altre_spese", entrata: "fattura_cliente", pagamento: "bonifico_diretto" };

export const NumeroConteggio = ({ c, mezzo, onSaved, disabled }) => {
  const [v, setV] = useState(c.numero || "");
  const save = async () => {
    if ((c.numero || "") === v.trim()) return;
    try { await api.put(`/conteggi/${c.padroncino_id}/numero`, { numero: v, mezzo: mezzo || null }); toast.success("Numero conteggio salvato"); onSaved(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <label className="flex items-center gap-1 text-sm" title="Numero del conteggio: resta lo stesso ogni mese">
      <span className="text-muted-foreground">N.</span>
      <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
             disabled={disabled} placeholder={c.mese.slice(2, 4)} className="h-7 w-16 font-mono text-center" data-testid="conteggi-numero" />
      <span className="font-mono text-muted-foreground">/{["GEN","FEB","MAR","APR","MAG","GIU","LUG","AGO","SET","OTT","NOV","DIC"][Number(c.mese.slice(5, 7)) - 1]}</span>
    </label>
  );
};

export const AddRigaDialog = ({ tipo, c, mezzo, riga, onClose, onSaved }) => {
  const [f, setF] = useState(riga ? { data: riga.data, categoria: riga.categoria, descrizione: riga.descrizione || "", importo: String(riga.importo) }
    : { data: lastDay(c.mese), categoria: DEF_CAT[tipo] || "altre_spese", descrizione: "", importo: "" });
  const save = async () => {
    try {
      if (riga) {
        await api.patch(`/movimenti/${riga.id}`, { data: f.data, mese_competenza: riga.mese_competenza || c.mese, categoria: f.categoria,
          descrizione: f.descrizione, importo: parseFloat(f.importo) });
        toast.success("Riga modificata (anche in Prima Nota)"); onSaved(); return;
      }
      await api.post("/movimenti", { company: c.azienda_code, data: f.data, mese_competenza: c.mese, tipo, categoria: f.categoria,
        descrizione: f.descrizione, importo: parseFloat(f.importo), padroncino_id: c.padroncino_id, fonte: "conteggio",
        ...(c.multi_mezzo ? { veicolo_id: mezzo || "generale" } : {}) });
      toast.success("Riga aggiunta (da riconciliare in Prima Nota)"); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{riga ? "Modifica riga" : `Aggiungi ${tipo === "spesa" ? "spesa" : tipo === "entrata" ? "entrata" : "pagamento"} al conteggio`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase font-heading">Data</Label><Input type="date" className="h-8 mt-1" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="riga-data" /></div>
          <div><Label className="text-xs uppercase font-heading">Importo (€)</Label><Input type="number" step="0.01" className="h-8 mt-1" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} data-testid="riga-importo" /></div>
          <div className="col-span-2"><Label className="text-xs uppercase font-heading">Categoria</Label>
            <Select value={f.categoria} onValueChange={(v) => setF({ ...f, categoria: v })}>
              <SelectTrigger className="h-8 mt-1" data-testid="riga-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORIE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2"><Label className="text-xs uppercase font-heading">Descrizione</Label><Input className="h-8 mt-1" value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} data-testid="riga-descrizione" /></div>
        </div>
        {!riga && <div className="text-xs text-muted-foreground">La riga va anche in Prima Nota come "da riconciliare": quando arriva il movimento dalla banca te lo propongo da abbinare.</div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={!f.descrizione || !(parseFloat(f.importo) > 0)} data-testid="riga-save">{riga ? "Salva" : "Aggiungi"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MovTable = ({ title, rows, neg, onAdd, onDelete, onEdit, onReload, onContributi, writable, tipo }) => {
  if ((!rows || rows.length === 0) && !writable) return null;
  const reali = rows.filter((r) => !r.virtuale);
  const sposta = async (m, dir) => {
    const ids = reali.map((r) => r.id);
    const i = ids.indexOf(m.id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await api.post("/conteggi/ordina", { ids }); onReload(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <div className="font-heading text-sm uppercase tracking-wider text-muted-foreground">{title}</div>
        {writable && <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={onAdd} data-testid={`conteggi-add-${tipo}`}><Plus className="w-3 h-3" />Aggiungi</Button>}
      </div>
      {rows.length === 0 ? <div className="text-xs text-muted-foreground py-1">Nessuna riga</div> : (
      <table className="autotrans-table w-full text-xs">
        <thead><tr><th className="w-24">Data</th><th className="w-40">Categoria</th><th>Descrizione</th><th className="text-right w-28">Importo</th><th className="w-24"></th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id} data-testid={`conteggi-riga-${m.id}`}>
              <td>{fmtDate(m.data)}</td>
              <td>{CATEGORIE_LABELS[m.categoria] || (m.categoria === "trattenuta" ? "Trattenuta su fatturato" : m.categoria)}</td>
              <td>{m.descrizione}
                {m.da_riconciliare && <Badge className="ml-2 h-4 text-[10px] bg-orange-500" data-testid={`conteggi-da-ric-${m.id}`}>da riconciliare</Badge>}
                {m.riconciliato_da && <Badge className="ml-2 h-4 text-[10px] bg-green-600">riconciliato con banca</Badge>}
                {m.gruppo && <Badge variant="outline" className="ml-2 h-4 text-[10px]" data-testid={`conteggi-gruppo-${m.id}`}>{m.gruppo.length} movimenti</Badge>}
                {m.virtuale && !m.gruppo && !m.contributi && <Badge variant="outline" className="ml-2 h-4 text-[10px]" data-testid="conteggi-trattenuta">automatica</Badge>}</td>
              <td className={`text-right font-mono ${neg ? "text-destructive" : "text-green-600"}`}>{m.contributi && writable ? (
                <Input type="number" step="0.01" defaultValue={m.importo || ""} placeholder="0,00" className="h-6 text-right font-mono text-xs"
                       onBlur={(e) => { if (parseFloat(e.target.value || 0) !== m.importo) onContributi(e.target.value); }} data-testid="conteggi-contributi-input" />
              ) : <>{neg ? "-" : "+"}{fmtEur(m.importo)}</>}</td>
              <td className="whitespace-nowrap">{writable && !m.virtuale && !m.contributi && <>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => sposta(m, -1)} disabled={reali[0]?.id === m.id} title="Sposta su" data-testid={`conteggi-su-${m.id}`}><ArrowUp className="w-3 h-3" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => sposta(m, 1)} disabled={reali[reali.length - 1]?.id === m.id} title="Sposta giù" data-testid={`conteggi-giu-${m.id}`}><ArrowDown className="w-3 h-3" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onEdit(m)} title="Modifica" data-testid={`conteggi-edit-${m.id}`}><Pencil className="w-3 h-3" /></Button></>}
                {writable && m.fonte === "conteggio" && <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => onDelete(m)} data-testid={`conteggi-del-${m.id}`}><Trash2 className="w-3 h-3" /></Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>)}
    </div>
  );
};
